import { ISlot, MaintenanceTimingEnum } from '../../../../organisms/bo_calendar_slot/types';
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import {
	calculateMarginFromMinutes,
	calculateMinutesFromHeight,
	DEFAULT_DND_STEP_LENGTH,
	isSameDate,
	oneHourWidth,
} from '../../../utils/timeUtils';
import { calculateOverallEndTime, getCombinedDateTimeString, MIDNIGHT } from '../../../../lib/timeUtils';
import { calculateTimeDifference, DateInput, DateTimeFormats, isLater, TimeUnit } from '@bondsports/date-time';
import { getMinutesDifferenceByStep } from '../../../utils';
import { getEffectiveEndTime, getEffectiveStartTime } from '../../../utils/effectiveSlotTimes';
import * as React from 'react';
import { ResizeCallbackData } from 'react-resizable';
import { maxBy } from 'lodash';
import { updateChildrenStartAndEndTimes, useMiddlewareContext } from '../../../hooks/useMiddlewareContext';
import dayjs from 'dayjs';

const YYYY_MM_DD_HH_MM_SS = 'YYYY-MM-DDTHH:mm:ss' as const;

const BEFORE_AND_AFTER_MAINTENANCE = [MaintenanceTimingEnum.AFTER, MaintenanceTimingEnum.BEFORE];

/** Min resize constraints for current hour density and DnD step. */
export const getResizeConstraints = (
	stepLengthMinutes: number = DEFAULT_DND_STEP_LENGTH
): [width: number, height: number] => {
	const safeStepLength = stepLengthMinutes > 0 ? stepLengthMinutes : DEFAULT_DND_STEP_LENGTH;
	const minPx = calculateMarginFromMinutes(safeStepLength);
	return [minPx, minPx];
};

const getBeforeAfterMaintenanceSizePx = (children: ISlot[] | undefined): number =>
	(children ?? [])
		.filter(child => BEFORE_AND_AFTER_MAINTENANCE.includes(Number(child.maintenanceTiming)))
		.map(child => {
			const duration: number = calculateTimeDifference(
				getCombinedDateTimeString(child.startDate, child.startTime),
				getCombinedDateTimeString(child.endDate, child.endTime),
				TimeUnit.MINUTE
			);
			return calculateMarginFromMinutes(duration);
		})
		.reduce((acc, curr) => acc + curr, 0);

function getDateValue(date: DateInput, time: string) {
	let [hours, minutes] = time.split(':');
	return dayjs(date, DateTimeFormats.YYYY_MM_DD).hour(Number(hours)).minute(Number(minutes)).toDate().getTime();
}

type OnResizeData = {
	type: string;
	data: {
		startDate: string;
		startTime: string;
		endDate: string;
		endTime: string;
		overId: string | number;
		parentId: string | number;
		resourceId: string | number;
		slotId: string;
	};
};

type CalculateSlotTimesResult = {
	startDate: string;
	startTime: string;
	endDate: string;
	endTime: string;
	margin: number;
	duration: number;
	maintenanceSize: number;
} | null;

type Props = {
	state: ISlot;
	currentDate: string;
	parentId: string | number;
	horizontal?: boolean;
	date?: string;
};

export const useResize = ({ currentDate, state, parentId, horizontal, date }: Props) => {
	const { getTopAndHeight, options, day: daysCount } = useMiddlewareContext();

	const selectedDate: string = date || currentDate;

	const calculateTopAndHeight = () => {
		const startTime: string = getEffectiveStartTime(state);
		const endTime: string = getEffectiveEndTime(state);
		const start: number = getDateValue(selectedDate, startTime);
		const end: number = getDateValue(selectedDate, endTime);

		return getTopAndHeight(start, end, horizontal, date);
	};

	const { top, height } = calculateTopAndHeight();

	let useSize: number = height;
	if (horizontal) {
		// calculate the width of the slot
		useSize = top + height > oneHourWidth * 24 * daysCount + 6 ? oneHourWidth * 24 * daysCount + 6 - top : height;
	}

	const ref = useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
	const [size, setSize] = useState<number>(useSize);

	const calculateSlotTimeFromSize = (size: number): CalculateSlotTimesResult => {
		// check if the size is a valid number
		if (!Number.isFinite(size) || Number.isNaN(size) || !size) {
			return null;
		}

		// split the start time into hours and minutes
		// if the slot is overnight, use midnight as the start time
		let [hours, minutes] = (isSameDate(state.startDate, selectedDate) ? state.startTime : MIDNIGHT)
			.split(':')
			.map(Number);

		let startTime: string;
		let startDate: string = dayjs(selectedDate, DateTimeFormats.YYYY_MM_DD)
			.hour(hours)
			.minute(minutes)
			.format(YYYY_MM_DD_HH_MM_SS);

		const maintenanceSize: number = getBeforeAfterMaintenanceSizePx(state.children);

		// calculate the new height by subtracting the maintenance height from the current height
		size = Math.floor(size - maintenanceSize);

		// calculates the slot duration in minutes from the slot's height
		minutes = Math.floor(calculateMinutesFromHeight(size));

		// rounds the minutes in options.dndStepLength (5 minutes by default)
		minutes = getMinutesDifferenceByStep(startDate, minutes, options.dndStepLength, true);

		// calculates the new height/width by the slot's new duration
		const margin: number = calculateMarginFromMinutes(minutes);

		let [endDate, endTime]: string[] = dayjs(startDate, YYYY_MM_DD_HH_MM_SS)
			.add(minutes, 'minute')
			.set('milliseconds', 0)
			.set('seconds', 0)
			.format(YYYY_MM_DD_HH_MM_SS)
			.split('T');

		[startDate, startTime] = startDate.split('T');

		// if the end date is later than the current date,
		// set the end time too midnight to cap the slot size
		if (isLater(endDate, selectedDate)) {
			endTime = MIDNIGHT;
		}

		return { startDate, startTime, endDate, endTime, margin, duration: minutes, maintenanceSize };
	};

	const onResize = (
		mouseEvent: React.MouseEvent<Element>,
		data: ResizeCallbackData,
		setCallback: Dispatch<SetStateAction<ISlot>>
	) => {
		// get the dimensions of the slot container element
		const { bottom, right } = ref.current.getBoundingClientRect();

		// calculate the difference between the mouse position and the bottom of the element
		const diff: number = horizontal ? mouseEvent.clientX - right : mouseEvent.clientY - bottom;

		const dimension: number = horizontal ? data.size.width : data.size.height;

		// calculate the new height/width by adding the difference to the current height
		const size: number = Math.floor(dimension + diff);

		// if the height/width is less than the minimum height, no need to update
		if (size === Math.floor(dimension)) {
			return;
		}

		const result: CalculateSlotTimesResult = calculateSlotTimeFromSize(size);

		if (!result) {
			return;
		}

		const { endTime, endDate, duration, margin, maintenanceSize } = result;
		const snappedSize: number = margin + maintenanceSize;

		setSize(snappedSize);
		if (ref.current) {
			ref.current.style.zIndex = '100';
		}

		if (endTime === state.endTime || duration < options.dndStepLength!) {
			return;
		}

		setCallback(slot => {
			const children: ISlot[] =
				state.children?.filter((child: ISlot) =>
					BEFORE_AND_AFTER_MAINTENANCE.includes(Number(child.maintenanceTiming))
				) ?? [];

			const maxEndDate: string = maxBy([state.endDate, ...children.map((child: ISlot) => child.endDate)])!;

			const startTime: string = isSameDate(state.startDate, selectedDate) ? state.startTime : MIDNIGHT;

			const currentDuration: number = calculateTimeDifference(
				getCombinedDateTimeString(date ?? currentDate, startTime),
				getCombinedDateTimeString(slot.endDate, slot.endTime),
				TimeUnit.MINUTE
			);

			updateChildrenStartAndEndTimes(
				state.children!.filter(child =>
					[MaintenanceTimingEnum.AFTER, MaintenanceTimingEnum.AT_THE_END].includes(Number(child.maintenanceTiming))
				),
				duration - currentDuration,
				dayjs(slot.startDate).diff(result.startDate, TimeUnit.DAY)
			);

			const overallEndTime: string = calculateOverallEndTime(
				[
					getCombinedDateTimeString(endDate, endTime),
					...children.map((child: ISlot) => getCombinedDateTimeString(child.endDate, child.endTime)),
				],
				maxEndDate,
				selectedDate
			);

			return {
				...slot,
				endTime,
				endDate,
				overallEndTime,
				children: structuredClone(slot.children),
			};
		});
	};

	const onResizeEnd = (callback: (id: number, slot: ISlot, data: OnResizeData) => void) => {
		const onChangeData: OnResizeData = {
			type: 'SLOT_RESIZE',
			data: {
				startDate: state.startDate,
				startTime: state.startTime,
				endDate: state.endDate,
				endTime: state.endTime,
				overId: parentId,
				parentId,
				resourceId: state.resourceId,
				slotId: String(state.id),
			},
		};

		ref.current.removeAttribute('style');
		setSize(useSize);

		callback(state.id, state, onChangeData);
	};

	useEffect(() => {
		// update the height of the container element
		setSize(useSize);
	}, [useSize]);

	return {
		ref,
		onResize,
		onResizeEnd,
		size,
		top,
	};
};
